import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Wrench, Plus, Edit, Trash2, Mail, Phone, Building, Search, Tag, GripVertical, X, Upload, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Emojis disponibles pour les catégories
const AVAILABLE_ICONS = ['🔧', '⚡', '🛗', '🔥', '🔑', '🎨', '🧹', '🌳', '🏢', '🚿', '🪟', '🚪', '🛠️', '🔨', '📋', '🏠', '💡', '❄️', '🔒', '🧰'];

const Prestataires = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('prestataires');
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [importing, setImporting] = useState(false);
  const [formData, setFormData] = useState({ nom: '', entreprise: '', email: '', telephone: '', specialite: '', adresse: '', siret: '' });
  const [categoryForm, setCategoryForm] = useState({ nom: '', icone: '📋' });
  const csvInputRef = useRef(null);

  useEffect(() => { fetchData(); }, [token]);

  const fetchData = async () => {
    try {
      const [providersRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/prestataires`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/categories`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProviders(providersRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Provider handlers
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProvider) {
        await axios.put(`${API}/prestataires/${editingProvider.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/prestataires`, formData, { headers: { Authorization: `Bearer ${token}` } });
      }
      setShowModal(false);
      setEditingProvider(null);
      setFormData({ nom: '', entreprise: '', email: '', telephone: '', specialite: '', adresse: '', siret: '' });
      fetchData();
    } catch (error) {
      console.error('Error saving provider:', error);
    }
  };

  const handleEdit = (provider) => {
    setEditingProvider(provider);
    setFormData({
      nom: provider.nom || '', entreprise: provider.entreprise || '', email: provider.email || '',
      telephone: provider.telephone || '', specialite: provider.specialite || '', adresse: provider.adresse || '', siret: provider.siret || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce prestataire ?')) {
      try {
        await axios.delete(`${API}/prestataires/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        fetchData();
      } catch (error) {
        console.error('Error deleting provider:', error);
      }
    }
  };

  // Category handlers
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, categoryForm, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/categories`, categoryForm, { headers: { Authorization: `Bearer ${token}` } });
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryForm({ nom: '', icone: '📋' });
      fetchData();
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  const handleEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({ nom: cat.nom || '', icone: cat.icone || '📋' });
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
      try {
        await axios.delete(`${API}/categories/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        fetchData();
      } catch (error) {
        console.error('Error deleting category:', error);
        if (error.response?.data?.detail) {
          alert(error.response.data.detail);
        }
      }
    }
  };

  // Export providers to CSV
  const handleExport = () => {
    const headers = ['Nom', 'Entreprise', 'Email', 'Téléphone', 'Spécialité', 'Adresse', 'SIRET'];
    const csvData = providers.map(p => [
      p.nom || '',
      p.entreprise || '',
      p.email || '',
      p.telephone || '',
      p.specialite || '',
      p.adresse || '',
      p.siret || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `prestataires_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Import providers from CSV
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    const reader = new FileReader();
    
    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        const dataLines = lines.slice(1);
        let imported = 0;
        let errors = 0;
        
        for (const line of dataLines) {
          const fields = line.match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g) || [];
          const cleanFields = fields.map(f => f.replace(/^"|"$/g, '').trim());
          
          if (cleanFields.length >= 3) {
            const [nom, entreprise, email, telephone, specialite, adresse, siret] = cleanFields;
            
            if (nom) {
              try {
                await axios.post(`${API}/prestataires`, {
                  nom,
                  entreprise: entreprise || '',
                  email: email || '',
                  telephone: telephone || '',
                  specialite: specialite || '',
                  adresse: adresse || '',
                  siret: siret || ''
                }, { headers: { Authorization: `Bearer ${token}` } });
                imported++;
              } catch (err) {
                errors++;
                console.error(`Error importing ${nom}:`, err);
              }
            }
          }
        }
        
        alert(`Import terminé: ${imported} prestataire(s) importé(s), ${errors} erreur(s)`);
        fetchData();
      } catch (error) {
        console.error('Error importing CSV:', error);
        alert('Erreur lors de l\'import du fichier CSV');
      } finally {
        setImporting(false);
        if (csvInputRef.current) csvInputRef.current.value = '';
      }
    };
    
    reader.readAsText(file);
  };

  // Download CSV template
  const downloadTemplate = () => {
    const headers = ['Nom', 'Entreprise', 'Email', 'Téléphone', 'Spécialité', 'Adresse', 'SIRET'];
    const example = ['Jean Dupont', 'Plomberie Express', 'contact@plomberie.fr', '0612345678', 'Plomberie', '15 rue de Paris', '12345678901234'];
    const csvContent = [headers.join(','), example.join(',')].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'modele_prestataires.csv';
    link.click();
  };

  const getCategoryIcon = (specialite) => {
    const cat = categories.find(c => c.nom === specialite);
    return cat?.icone || '📋';
  };

  const getSpecialiteColor = (spec) => {
    const colors = {
      'Plomberie': 'bg-blue-100 text-blue-700', 'Électricité': 'bg-yellow-100 text-yellow-700',
      'Chauffage/Climatisation': 'bg-orange-100 text-orange-700', 'Serrurerie': 'bg-gray-100 text-gray-700',
      'Peinture/Ravalement': 'bg-purple-100 text-purple-700', 'Espaces verts': 'bg-green-100 text-green-700',
      'Nettoyage/Entretien': 'bg-cyan-100 text-cyan-700', 'Ascenseur': 'bg-indigo-100 text-indigo-700',
      'Parties communes': 'bg-slate-100 text-slate-700'
    };
    return colors[spec] || 'bg-gray-100 text-gray-700';
  };

  const filteredProviders = providers.filter(p =>
    `${p.nom} ${p.entreprise} ${p.specialite}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div></div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="prestataires-page">
      {/* Hidden CSV input */}
      <input
        type="file"
        ref={csvInputRef}
        onChange={handleImport}
        accept=".csv"
        className="hidden"
      />

      {/* Header - Style unifié */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#00D3A8]/10 rounded-xl">
            <Wrench className="h-8 w-8 text-[#00D3A8]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mes Prestataires</h1>
            <p className="text-gray-500">Gérez vos prestataires et leurs spécialités</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => csvInputRef.current?.click()}
            disabled={importing}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
            data-testid="import-providers-btn"
          >
            <Upload size={16} />
            <span>{importing ? 'Import...' : 'Importer'}</span>
          </button>
          <button
            onClick={handleExport}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
            data-testid="export-providers-btn"
          >
            <Download size={16} />
            <span>Exporter</span>
          </button>
        </div>
      </div>

      {/* Tabs - Style unifié avec coins arrondis en haut */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-1">
          <button
            onClick={() => setActiveTab('prestataires')}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-all rounded-t-lg ${
              activeTab === 'prestataires' 
                ? 'bg-[#00D3A8] text-white' 
                : 'text-gray-600 hover:bg-gray-100 bg-gray-50'
            }`}
            data-testid="tab-prestataires"
          >
            <Wrench size={16} />
            Prestataires
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-all rounded-t-lg ${
              activeTab === 'categories' 
                ? 'bg-[#00D3A8] text-white' 
                : 'text-gray-600 hover:bg-gray-100 bg-gray-50'
            }`}
            data-testid="tab-categories"
          >
            <Tag size={16} />
            Catégories
          </button>
        </div>
      </div>

      {/* Prestataires Tab */}
      {activeTab === 'prestataires' && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input 
                type="text" 
                placeholder="Rechercher un prestataire..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none text-sm md:text-base" 
              />
            </div>
            <button 
              onClick={() => { setEditingProvider(null); setFormData({ nom: '', entreprise: '', email: '', telephone: '', specialite: categories[0]?.nom || '', adresse: '', siret: '' }); setShowModal(true); }}
              className="bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] hover:opacity-90 text-white py-2 px-5 rounded-full flex items-center justify-center space-x-2 font-medium shadow-md transition-all text-sm md:text-base whitespace-nowrap"
              data-testid="add-provider-btn"
            >
              <Plus size={20} /><span className="hidden sm:inline">Nouveau prestataire</span><span className="sm:hidden">Nouveau</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mt-6">
            {filteredProviders.map((provider) => (
              <div key={provider.id} className="bg-white rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-100 p-4 md:p-6 hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] transition-shadow" data-testid={`provider-card-${provider.id}`}>
                <div className="flex items-start justify-between mb-3 md:mb-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-[#00D3A8]/20 to-[#00D3A8]/10 rounded-xl flex items-center justify-center text-xl md:text-2xl">
                    {getCategoryIcon(provider.specialite)}
                  </div>
                  <div className="flex space-x-1 md:space-x-2">
                    <button onClick={() => handleEdit(provider)} className="text-gray-400 hover:text-[#00D3A8] transition-colors p-1"><Edit size={18} /></button>
                    <button onClick={() => handleDelete(provider.id)} className="text-gray-400 hover:text-red-500 transition-colors p-1"><Trash2 size={18} /></button>
                  </div>
                </div>
                <h3 className="font-semibold text-gray-800 font-[Manrope] text-sm md:text-base">{provider.nom}</h3>
                {provider.entreprise && <p className="text-xs md:text-sm text-gray-500 flex items-center mt-1"><Building size={14} className="mr-1" />{provider.entreprise}</p>}
                <span className={`inline-block mt-2 text-xs px-2.5 py-0.5 rounded-full font-medium ${getSpecialiteColor(provider.specialite)}`}>
                  {getCategoryIcon(provider.specialite)} {provider.specialite}
                </span>
                <div className="mt-4 pt-4 border-t border-gray-100 text-sm text-gray-500 space-y-1">
                  <p className="flex items-center"><Mail size={14} className="mr-2 text-gray-400" />{provider.email}</p>
                  {provider.telephone && <p className="flex items-center"><Phone size={14} className="mr-2 text-gray-400" />{provider.telephone}</p>}
                </div>
              </div>
            ))}
          </div>

          {filteredProviders.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Wrench size={48} className="mx-auto mb-4 text-gray-300" />
              <p>Aucun prestataire trouvé</p>
            </div>
          )}
        </>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-gray-500">Gérez les catégories de prestataires et d'incidents</p>
            <button 
              onClick={() => { setEditingCategory(null); setCategoryForm({ nom: '', icone: '📋' }); setShowCategoryModal(true); }}
              className="bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] hover:opacity-90 text-white py-2 px-5 rounded-full flex items-center space-x-2 font-medium shadow-md transition-all"
              data-testid="add-category-btn"
            >
              <Plus size={20} /><span>Nouvelle catégorie</span>
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Icône</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Nom de la catégorie</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Prestataires</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat, index) => {
                  const providerCount = providers.filter(p => p.specialite === cat.nom).length;
                  return (
                    <tr key={cat.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors" data-testid={`category-row-${cat.id}`}>
                      <td className="py-3 px-4">
                        <span className="text-2xl">{cat.icone}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-800">{cat.nom}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-gray-500">{providerCount} prestataire{providerCount !== 1 ? 's' : ''}</span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button 
                            onClick={() => handleEditCategory(cat)} 
                            className="text-gray-400 hover:text-[#00D3A8] transition-colors p-1"
                            data-testid={`edit-category-${cat.id}`}
                          >
                            <Edit size={18} />
                          </button>
                          {cat.nom !== 'Autre' && (
                            <button 
                              onClick={() => handleDeleteCategory(cat.id)} 
                              className="text-gray-400 hover:text-red-500 transition-colors p-1"
                              data-testid={`delete-category-${cat.id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {categories.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Tag size={48} className="mx-auto mb-4 text-gray-300" />
              <p>Aucune catégorie</p>
            </div>
          )}
        </>
      )}

      {/* Provider Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="provider-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold font-[Manrope] text-gray-800">{editingProvider ? 'Modifier le prestataire' : 'Nouveau prestataire'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input type="text" value={formData.nom} onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise</label>
                  <input type="text" value={formData.entreprise} onChange={(e) => setFormData({...formData, entreprise: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input type="tel" value={formData.telephone} onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Spécialité *</label>
                <select value={formData.specialite} onChange={(e) => setFormData({...formData, specialite: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none" required>
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.nom}>{c.icone} {c.nom}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <input type="text" value={formData.adresse} onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SIRET</label>
                <input type="text" value={formData.siret} onChange={(e) => setFormData({...formData, siret: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none" />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                <button type="button" onClick={() => setShowModal(false)} className="px-5 py-2 border border-gray-200 rounded-full hover:bg-gray-50 transition-all font-medium">Annuler</button>
                <button type="submit" className="px-5 py-2 bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] text-white rounded-full hover:opacity-90 transition-all font-medium shadow-md">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl" data-testid="category-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold font-[Manrope] text-gray-800">{editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</h2>
              <button onClick={() => setShowCategoryModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
            </div>
            <form onSubmit={handleCategorySubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la catégorie *</label>
                <input 
                  type="text" 
                  value={categoryForm.nom} 
                  onChange={(e) => setCategoryForm({...categoryForm, nom: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none" 
                  required 
                  placeholder="Ex: Plomberie, Électricité..."
                  data-testid="category-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Icône</label>
                <div className="grid grid-cols-10 gap-2">
                  {AVAILABLE_ICONS.map(icon => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setCategoryForm({...categoryForm, icone: icon})}
                      className={`text-2xl p-2 rounded-lg transition-all ${
                        categoryForm.icone === icon 
                          ? 'bg-[#00D3A8]/20 ring-2 ring-[#00D3A8]' 
                          : 'hover:bg-gray-100'
                      }`}
                      data-testid={`icon-${icon}`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                <button type="button" onClick={() => setShowCategoryModal(false)} className="px-5 py-2 border border-gray-200 rounded-full hover:bg-gray-50 transition-all font-medium">Annuler</button>
                <button type="submit" className="px-5 py-2 bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] text-white rounded-full hover:opacity-90 transition-all font-medium shadow-md" data-testid="save-category-btn">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Prestataires;
